import React, { useCallback, useEffect, useState } from 'react';
import { FaCommentAlt } from 'react-icons/fa';
import { IoMail, IoTicketSharp } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Input, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import Spinner from '../../../components/bootstrap/Spinner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import FileUpload from '../../../components/FileUpload/FileUpload';
import CustomSelect from '../../../components/forms/CustomSelect';
import ModalForm from '../../../components/forms/ModalForm';
import SendClientEmailForm from '../../../components/forms/SendClientEmailForm';
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFundaeGroupings from '../../../hooks/api-calls/useFundaeGroupings';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { setNavigationContext, setSelectedView } from '../../../redux/clientProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { ClientService } from '../../../services/client/clientService';
import { ClientApiResponse } from '../../../type/entities/client-type';
import {
    CREDIT_DOCUMENTS_UPLOAD_TITLE,
    validateCreditDocumentsRequired,
} from '../utils/creditDocumentsValidation';
import { AddCommentModal } from '../list/components/AddCommentModal';
import { BiSolidPhoneCall } from 'react-icons/bi';
import CallModalForm from '../../contracts/components/CallModalForm';
import {ArrowBack} from "@mui/icons-material";

type ClientProfileLocationState = {
    fromContract?: boolean;
    organisingCorporationId?: string | null;
    returnToContractPath?: string;
};

interface CreditPreviewContract {
    id: string;
    code: string;
}

interface CreditIncidencePreview {
    incidencesToGenerate: number;
    contracts: CreditPreviewContract[];
}

interface SelectOption {
    value: string;
    label: string;
    organisingCorporationId?: string | null;
}

interface ClientProfileLayoutProps {
    isLoading?: boolean;
    children: React.ReactNode;
    onCreditsUpdated?: () => void;
}

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.clients.profile.info,
        permission: {
            group: 'clients',
            action: 'get_clients',
        },
    },
    {
        label: 'Empleados',
        path: menuRoutes.clients.profile.students,
        permission: {
            group: 'clients',
            action: 'get_clients_students',
        },
    },
    {
        label: 'Contratos',
        path: menuRoutes.clients.profile.contracts,
        permission: {
            group: 'clients',
            action: 'get_clients_contracts',
        },
    },
    {
        label: 'Asesoría',
        path: menuRoutes.clients.profile.consultancy,
        permission: {
            group: 'clients',
            action: 'get_clients_consultancy',
        },
    },
    {
        label: 'Facturación',
        path: menuRoutes.clients.profile.invoices,
        permission: {
            group: 'clients',
            action: 'get_clients_invoicing',
        },
    },
    {
        label: 'Llamadas',
        path: menuRoutes.clients.profile.calls,
        permission: {
            group: 'clients',
            action: 'get_clients_calls',
        },
    },
    {
        label: 'Comentarios',
        path: menuRoutes.clients.profile.comments,
        permission: {
            group: 'clients',
            action: 'get_clients_comments',
        },
    },
    {
        label: 'Créditos',
        path: menuRoutes.clients.profile.creditHistory,
        permission: {
            group: 'clients',
            action: 'get_clients_credit_history',
        },
    },
    {
        label: 'Actividad',
        path: menuRoutes.clients.profile.activityHistory,
        permission: {
            group: 'clients',
            action: 'get_clients_activity_history',
        },
    },
    {
        label: 'Redes comerciales',
        path: menuRoutes.clients.profile.commercialNetworks,
        permission: {
            group: 'clients',
            action: 'manage_clients_commercial_networks',
        },
    }
];

export default function ClientProfileLayout({ isLoading, children, onCreditsUpdated }: ClientProfileLayoutProps) {

    const dispatch = useDispatch();
    const { selectedView, navigationClientId, navigationOrganisingCorporationId } = useSelector((state: RootState) => state.clientProfile);
    const { id = '' } = useParams<{ id: string }>();
    const location = useLocation();
    const { userCan, isFacilitatorManager } = usePrivilege();
    const canViewContracts = userCan('get_contracts', 'contracts');
    const canEditClients = userCan('edit_clients', 'clients') && !isFacilitatorManager;
    const canDeleteClients = userCan('delete_clients', 'clients') && !isFacilitatorManager;
    const canAddCredits = userCan('add_client_credits', 'clients') && !isFacilitatorManager;
    const canManageClientEmails = userCan('manage_company_email_templates', 'user') && !isFacilitatorManager;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const profileLocationState = (location.state as ClientProfileLocationState | null) ?? null;
    const fallbackOrganisingCorporationId = profileLocationState?.organisingCorporationId
        ?? (navigationClientId === id ? navigationOrganisingCorporationId : null);
    const { handleErrors } = useHandleErrors();
    const { getFundaeGroupingsList, updateFundaeGroupingsHookFilters } = useFundaeGroupings(
        fallbackOrganisingCorporationId ? { organising_corporation_id: fallbackOrganisingCorporationId } : {}
    );
    const service = new ClientService();
    const showBackToContract = Boolean(profileLocationState?.fromContract && profileLocationState?.returnToContractPath);
    const sourceOrderId = searchParams.get('fromOrderId');
    const sourceOrderView = searchParams.get('fromOrderView');
    const backToCampaignPath = sourceOrderId && sourceOrderView === 'campaigns'
        ? `${menuRoutes.orders.path}/${sourceOrderId}/profile${menuRoutes.orders.profile.campaigns}`
        : null;

    const [loading, setLoading] = useState<boolean>(false);
    const [rowId, setRowId] = useState<string>('');
    const [openEmailTemplateModal, setOpenEmailTemplateModal] = useState<boolean>(false);
    const [openCreditModal, setOpenCreditModal] = useState<boolean>(false);
    const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
    const [openCommentModal, setOpenCommentModal] = useState<boolean>(false);
    const [credits, setCredits] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [fundaeGroupingId, setFundaeGroupingId] = useState<string>('');
    const [selectedContractIds, setSelectedContractIds] = useState<string[]>([]);
    const [clientContractsOptions, setClientContractsOptions] = useState<SelectOption[]>([]);
    const [isLoadingContracts, setIsLoadingContracts] = useState<boolean>(false);
    const [sendIncidenceEmails, setSendIncidenceEmails] = useState<boolean>(false);
    const [creditIncidencePreview, setCreditIncidencePreview] = useState<CreditIncidencePreview | null>(null);
    const [creditUpdateFiles, setCreditUpdateFiles] = useState<File[]>([]);
    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [phone, setPhone] = useState<string>('');
    const [phoneSecondary, setPhoneSecondary] = useState<string>('');
    const [mobile, setMobile] = useState<string>('');
    const [openCallModal, setOpenCallModal] = useState<boolean>(false);

    const [data, loadingClient, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getClientById(id);
        return response.getResponseData() as ClientApiResponse;
    }, [id]));

    const pageHeader = {
        title: `Perfil de ${data?.companyName || 'Cliente'}`,
        breadcrumb: [
            { name: 'Ventas' },
            { name: 'Gestión de clientes' },
            { name: 'Clientes', href: menuRoutes.clients.path },
            { name: 'Perfil de Cliente' },
        ],
    };

    const getDefaultCreditDateRange = () => {
        const currentYear = new Date().getFullYear();
        return {
            startDate: `${currentYear}-01-01`,
            endDate: `${currentYear}-12-31`,
        };
    };

    const resetCreditForm = () => {
        setCredits('');
        setStartDate('');
        setEndDate('');
        setFundaeGroupingId('');
        setSelectedContractIds([]);
        setSendIncidenceEmails(false);
        setCreditIncidencePreview(null);
        setCreditUpdateFiles([]);
    };

    const closeCreditModal = () => {
        setOpenCreditModal(false);
        resetCreditForm();
    };

    const closeConfirmModal = (reopenCreditModal: boolean = false) => {
        setOpenConfirmModal(false);
        setCreditIncidencePreview(null);
        setSendIncidenceEmails(false);
        if (reopenCreditModal) {
            setOpenCreditModal(true);
        }
    };

    const handleOpenCreditModal = (clientId: string) => {
        const defaults = getDefaultCreditDateRange();
        setRowId(clientId);
        setCredits('');
        setStartDate(defaults.startDate);
        setEndDate(defaults.endDate);
        setFundaeGroupingId('');
        setSelectedContractIds([]);
        setSendIncidenceEmails(false);
        setCreditIncidencePreview(null);
        setCreditUpdateFiles([]);
        loadClientContractsForCreditsUpdate(clientId);
        setOpenCreditModal(true);
    };

    const loadClientContractsForCreditsUpdate = async (clientId: string) => {
        if (!clientId) {
            return;
        }

        setIsLoadingContracts(true);
        try {
            const response = await (await service.listContracts({
                page: 1,
                limit: 999999,
                filter_filters: { client: clientId },
                filter_order: [{ field: 'createdAt', order: 'desc' }],
            })).getResponseData();

            if (!response?.success) {
                handleErrors(response);
                setClientContractsOptions([]);
                return;
            }

            const mappedOptions = Array.isArray(response?.data?.data)
                ? response.data.data
                    .filter((contract: any) => contract?.id && contract?.code)
                    .map((contract: any) => ({
                        value: contract.id,
                        label: contract.code,
                        organisingCorporationId: contract?.organisingCorporation?.id || contract?.organisingCorporationId || null,
                    }))
                : [];

            setClientContractsOptions(mappedOptions);
        } catch (error: any) {
            toast.error(error?.message ?? 'No se pudieron cargar los contratos del cliente');
            setClientContractsOptions([]);
        } finally {
            setIsLoadingContracts(false);
        }
    };

    const validateFundaeGroupingAssignment = (): boolean => {
        const hasFundaeGrouping = Boolean(fundaeGroupingId);
        const hasContractsSelected = selectedContractIds.length > 0;

        if (hasFundaeGrouping !== hasContractsSelected) {
            toast.error('Debes seleccionar agrupación fundae y contrato/s para aplicar la asignación');
            return false;
        }

        return true;
    };

    const submitCreditsUpdate = async (parsedCredits: number, sendIncidenceEmailsValue: boolean) => {
        const response = await (await service.addCredits(
            rowId,
            parsedCredits,
            startDate,
            endDate,
            sendIncidenceEmailsValue,
            fundaeGroupingId || undefined,
            selectedContractIds,
            creditUpdateFiles
        )).getResponseData();
        if (response.success) {
            setOpenConfirmModal(false);
            closeCreditModal();
            toast.success('Créditos actualizados correctamente');
            refetch();
            onCreditsUpdated?.();
        } else {
            handleErrors(response);
        }
    };

    const addCredits = async () => {
        const parsedCredits = parseFloat(credits);
        if (credits.trim() === '' || Number.isNaN(parsedCredits)) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }

        if (parsedCredits < 0) {
            toast.error('La cantidad de créditos no puede ser negativa');
            return;
        }

        if (!validateFundaeGroupingAssignment()) {
            return;
        }

        if (!validateCreditDocumentsRequired(creditUpdateFiles)) {
            return;
        }

        setLoading(true);
        const previewResponse = await (await service.previewAddCredits(
            rowId,
            parsedCredits,
            startDate,
            endDate,
            fundaeGroupingId || undefined,
            selectedContractIds
        )).getResponseData();
        if (!previewResponse.success) {
            handleErrors(previewResponse);
            setLoading(false);
            return;
        }

        const previewData = previewResponse.data || {};
        if (previewData.willGenerateIncidences) {
            const contracts: CreditPreviewContract[] = Array.isArray(previewData.contracts)
                ? previewData.contracts
                    .filter((contract: any) => contract && typeof contract.code === 'string' && contract.code.trim() !== '')
                    .map((contract: any) => ({
                        id: typeof contract.id === 'string' ? contract.id : '',
                        code: contract.code,
                    }))
                : [];
            setCreditIncidencePreview({
                incidencesToGenerate: previewData.incidencesToGenerate || 0,
                contracts,
            });
            setOpenCreditModal(false);
            setOpenConfirmModal(true);
            setLoading(false);
            return;
        }

        await submitCreditsUpdate(parsedCredits, false);
        setLoading(false);
    };

    const confirmCreditsUpdate = async () => {
        const parsedCredits = parseFloat(credits);
        if (credits.trim() === '' || Number.isNaN(parsedCredits) || parsedCredits < 0) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }
        if (!validateFundaeGroupingAssignment()) {
            return;
        }

        if (!validateCreditDocumentsRequired(creditUpdateFiles)) {
            return;
        }

        setLoading(true);
        await submitCreditsUpdate(parsedCredits, sendIncidenceEmails);
        setLoading(false);
    };

    const toggleState = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleClientState(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el cliente" : "Se ha activado el cliente");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteClient(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.clients.path);
                toast.success('Cliente eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        navigate(`${menuRoutes.clients.path}/${id}/profile${selectedView}${location.search}`, { state: location.state });
    }, [id, location.search, location.state, navigate, selectedView]);

    useEffect(() => {
        const incomingOrganisingCorporationId = profileLocationState?.organisingCorporationId;

        if (navigationClientId !== id) {
            dispatch(setNavigationContext({
                clientId: id || null,
                organisingCorporationId: incomingOrganisingCorporationId ?? null,
            }));
            return;
        }

        if (incomingOrganisingCorporationId !== undefined && incomingOrganisingCorporationId !== navigationOrganisingCorporationId) {
            dispatch(setNavigationContext({
                clientId: id || null,
                organisingCorporationId: incomingOrganisingCorporationId ?? null,
            }));
        }
    }, [dispatch, id, navigationClientId, navigationOrganisingCorporationId, profileLocationState?.organisingCorporationId]);

    useEffect(() => {
        const selectedContracts = clientContractsOptions.filter((option) => selectedContractIds.includes(option.value));
        const uniqueOrganisingCorporationIds = Array.from(
            new Set(
                selectedContracts
                    .map((option) => option.organisingCorporationId)
                    .filter((value): value is string => Boolean(value))
            )
        );

        updateFundaeGroupingsHookFilters({
            organising_corporation_id: uniqueOrganisingCorporationIds.length === 1
                ? uniqueOrganisingCorporationIds[0]
                : uniqueOrganisingCorporationIds.length === 0
                    ? fallbackOrganisingCorporationId
                    : null
        });
    }, [selectedContractIds, clientContractsOptions, fallbackOrganisingCorporationId, updateFundaeGroupingsHookFilters]);

    return (
        <div className='overflow-visible'>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {backToCampaignPath && (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(backToCampaignPath)}
                        >
                            <ArrowBack/> Volver a campaña
                        </Button>
                    )}
                    {showBackToContract && (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(profileLocationState?.returnToContractPath || menuRoutes.contracts.path)}
                        >
                            <ArrowBack/> Volver al contrato
                        </Button>
                    )}

                    {canEditClients && (
                        <>
                            {data && canManageClientEmails && (
                                <>
                                    <Tooltip
                                        size="sm"
                                        content={'Enviar correo electrónico'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Button
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => { setRowId(data.id); setOpenEmailTemplateModal(true) }}
                                        >
                                            <IoMail className="h-4 w-4" size={20} />
                                        </Button>
                                    </Tooltip>
                                    <div className='border-l border-gray-300' />
                                </>
                            )}

                            {data && userCan('realize_calls', 'calls') && (
                                <>
                                    {(() => {
                                        const hasPhone = Boolean(data.telephone && data.telephone.trim() !== "");
                                        const hasPhoneSecondary = Boolean(data.telephoneSecondary && data.telephoneSecondary.trim() !== "");
                                        const hasMobile = Boolean(data.mobile && data.mobile.trim() !== "");
                                        const hasAnyPhone = hasPhone || hasPhoneSecondary || hasMobile;

                                        return (
                                            <Tooltip
                                                size="sm"
                                                content={
                                                    hasAnyPhone
                                                        ? 'Realizar llamada'
                                                        : 'El cliente no posee número de teléfono'
                                                }
                                                placement="top"
                                                color={hasAnyPhone ? "invert" : "danger"}
                                            >
                                                <Button
                                                    variant="outline"
                                                    disabled={!hasAnyPhone}
                                                    className={
                                                        hasAnyPhone
                                                            ? "hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                            : "opacity-50 cursor-not-allowed"
                                                    }
                                                    onClick={() => {
                                                        if (!hasAnyPhone) return;
                                                        setPhone(data.telephone);
                                                        setPhoneSecondary(data.telephoneSecondary);
                                                        setMobile(data.mobile);
                                                        setOpenCallModal(true);
                                                    }}
                                                >
                                                    <BiSolidPhoneCall className="h-4 w-4" size={20} />
                                                </Button>
                                            </Tooltip>
                                        );
                                    })()}
                                    <div className='border-l border-gray-300' />
                                </>
                            )}


                            {data && canAddCredits && (
                                <>
                                    <Tooltip
                                        size="sm"
                                        content={'Añadir créditos'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Button
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleOpenCreditModal(data.id)}
                                            disabled={loading}
                                        >
                                            {loading ? <Spinner color={'dark'} /> : <IoTicketSharp className="h-4 w-4" size={20} />}
                                        </Button>
                                    </Tooltip>
                                    <div className='border-l border-gray-300' />
                                </>
                            )}

                            {data && (
                                <Tooltip
                                    size="sm"
                                    content={data.active ? 'Desactivar cliente' : 'Activar cliente'}
                                    placement="top"
                                    color="invert"
                                >
                                    <Switch
                                        id={id}
                                        label={data.active ? 'Activo' : 'Desactivado'}
                                        checked={data.active}
                                        onChange={() => toggleState(data.id, data.active)}
                                        switchKnobClassName='bg-secondary'
                                    />
                                </Tooltip>
                            )}

                            <div className='border-l border-gray-300' />

                            <Tooltip content={'Editar cliente'} placement={'top'} color={'invert'} size={'sm'}>
                                <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                    navigate(`${menuRoutes.clients.path}/${id}/edit`)
                                }}>
                                    Editar
                                </Button>
                            </Tooltip>
                        </>
                    )}

                    {data && userCan('create_client_comments', 'clients') && (
                        <>
                            <Tooltip
                                size="sm"
                                content={'Añadir comentario'}
                                placement="top"
                                color="invert"
                            >
                                <Button
                                    variant="outline"
                                    className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                    onClick={() => { setOpenCommentModal(true) }}
                                >
                                    <FaCommentAlt className="h-4 w-4" size={20} />
                                </Button>
                            </Tooltip>
                            <div className='border-l border-gray-300' />
                        </>
                    )}

                    {canDeleteClients && (
                        <DeletePopover
                            title={`Eliminar cliente`}
                            description={`¿Estás seguro de que deseas eliminar el cliente?`}
                            size={'md'}
                            onDelete={() => handleDelete(id)}
                            actionIconClassName='h-auto w-10'
                        />
                    )}
                </div>
            </PageHeader>

            <ModalForm isOpen={openCreditModal} onClose={closeCreditModal} title='Añadir actualización de créditos'>
                <Input
                    label='Créditos'
                    type='number'
                    value={credits}
                    onChange={(e) => setCredits(e.target.value)}
                />

                <Input
                    label='Fecha de inicio de validez de los créditos'
                    type='date'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />

                <Input
                    label='Fecha de fin de validez de los créditos'
                    type='date'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />

                <div className='pt-4'>
                    <CustomSelect
                        id='fundaeGroupingId'
                        label='Agrupación Fundae'
                        options={getFundaeGroupingsList()}
                        value={fundaeGroupingId
                            ? getFundaeGroupingsList().find((grouping: SelectOption) => grouping.value === fundaeGroupingId) || null
                            : null}
                        onChange={(value: any) => setFundaeGroupingId(value?.value || '')}
                        placeholder='Selecciona agrupación fundae'
                        isSearchable
                        isClearable
                    />
                </div>

                <div className='pt-4'>
                    <CustomSelect
                        id='contractIds'
                        label='Contrato/s a aplicar'
                        options={clientContractsOptions}
                        value={clientContractsOptions.filter((option) => selectedContractIds.includes(option.value))}
                        onChange={(value: any) => {
                            const ids = Array.isArray(value) ? value.map((item) => item.value) : [];
                            setSelectedContractIds(ids);
                        }}
                        placeholder='Selecciona contrato/s'
                        isSearchable
                        isClearable
                        isMulti
                        isLoading={isLoadingContracts}
                    />
                </div>

                <div className='pt-4'>
                    <FileUpload
                        files={creditUpdateFiles}
                        onFilesChange={setCreditUpdateFiles}
                        title={CREDIT_DOCUMENTS_UPLOAD_TITLE}
                        dropText='Arrastra archivos aquí o haz clic para seleccionarlos'
                        dragActiveText='Suelta los archivos aquí...'
                        maxFiles={50}
                        maxSize={52428800}
                        showFileList
                        requiredAsterisk={true}
                    />
                </div>

                <div className='flex justify-center p-7'>
                    <Button
                        disabled={loading || !startDate || !endDate || credits.trim() === '' || creditUpdateFiles.length === 0}
                        onClick={() => addCredits()}
                        color='primary'
                    >
                        Añadir créditos
                    </Button>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={openConfirmModal}
                onClose={() => closeConfirmModal(true)}
                title='Confirmar actualización de créditos'
            >
                <div className='space-y-4 pb-6'>
                    <p className='text-sm text-gray-700'>
                        Esta actualización generará {creditIncidencePreview?.incidencesToGenerate || 0} incidencia(s). ¿Deseas continuar?
                    </p>

                    {creditIncidencePreview?.contracts && creditIncidencePreview.contracts.length > 0 && (
                        <div>
                            <p className='text-sm font-medium mb-2'>Contratos vinculados</p>
                            <div className='flex flex-wrap gap-2'>
                                {creditIncidencePreview.contracts.map((contract, index) => (
                                    canViewContracts && contract.id
                                        ? (
                                            <Link
                                                key={contract.id}
                                                to={`${menuRoutes.contracts.path}/${contract.id}/profile${menuRoutes.contracts.profile.info}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-primary hover:bg-gray-50'
                                            >
                                                {contract.code}
                                            </Link>
                                        )
                                        : (
                                            <span
                                                key={`${contract.id || 'contract'}-${contract.code}-${index}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-gray-700'
                                            >
                                                {contract.code}
                                            </span>
                                        )
                                ))}
                            </div>
                        </div>
                    )}

                    <div className='pt-2'>
                        <Switch
                            label='Enviar correo a responsables/participantes si se registran incidencias'
                            checked={sendIncidenceEmails}
                            onChange={() => setSendIncidenceEmails(!sendIncidenceEmails)}
                            switchKnobClassName='bg-secondary'
                            switchClassName='w-[65px]'
                        />
                    </div>

                    <div className='flex justify-end gap-3 pt-2'>
                        <Button variant='outline' onClick={() => closeConfirmModal(true)} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button color='secondary' onClick={confirmCreditsUpdate} isLoading={loading} disabled={loading}>
                            Confirmar y actualizar
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <CallModalForm isOpen={openCallModal} onClose={() => setOpenCallModal(false)} refetch={refetch} currentClientId={data?.id || rowId} phone={phone} phoneSecondary={phoneSecondary} mobile={mobile} />

            <ModalForm size='xl' isOpen={openEmailTemplateModal} onClose={() => setOpenEmailTemplateModal(false)} title='Enviar correo electrónico al cliente'>
                <SendClientEmailForm onClose={() => setOpenEmailTemplateModal(false)} clientId={data?.id || ''} />
            </ModalForm>

            <AddCommentModal service={(new ClientService).addComment} id={rowId} isOpen={openCommentModal} onClose={() => setOpenCommentModal(false)} refetch={refetch} />

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : children}
        </div>
    );
}
